import type { CallInfo } from '../sip/SipClient';
import { loadArray, save } from './storage';

export type HistoryStatus = 'answered' | 'missed' | 'rejected' | 'cancelled' | 'failed' | 'transferred';

export interface HistoryEntry {
  id: string;
  direction: 'incoming' | 'outgoing';
  number: string;
  name: string;
  startedAt: number;
  answeredAt?: number;
  endedAt: number;
  durationSec: number;
  status: HistoryStatus;
  cause?: string;
}

const KEY = 'ngnphone.history';
const MAX = 500;

export function toHistoryEntry(c: CallInfo): HistoryEntry {
  const endedAt = c.endedAt ?? Date.now();
  let status: HistoryStatus;
  if (c.answeredAt) status = c.transfer?.status === 'accepted' ? 'transferred' : 'answered';
  else if (c.direction === 'incoming') status = c.endedBy === 'local' ? 'rejected' : 'missed';
  else status = c.endedBy === 'local' ? 'cancelled' : 'failed';
  return {
    id: c.id,
    direction: c.direction,
    number: c.number,
    name: c.name,
    startedAt: c.startedAt,
    answeredAt: c.answeredAt,
    endedAt,
    durationSec: c.answeredAt ? Math.round((endedAt - c.answeredAt) / 1000) : 0,
    status,
    cause: c.endCause,
  };
}

class HistoryStore {
  private entries: HistoryEntry[] = loadArray<HistoryEntry>(KEY);
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = () => this.entries;

  private set(entries: HistoryEntry[]) {
    this.entries = entries;
    save(KEY, entries);
    this.listeners.forEach((l) => l());
  }

  add(c: CallInfo) {
    if (this.entries.some((e) => e.id === c.id)) return;
    this.set([toHistoryEntry(c), ...this.entries].slice(0, MAX));
  }

  remove(id: string) {
    this.set(this.entries.filter((e) => e.id !== id));
  }

  clear() {
    this.set([]);
  }
}

export const history = new HistoryStore();
